import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import qs from "qs";
import { ApiConstant, LangConstant, SystemConstant } from "const";
import { ApiUtils, HumpUtils, SystemUtils } from "utils";
import { formatArray2Key, formatPagingParams } from "utils/common.utils";
import { debounce } from "lodash";

const MAX_FETCH_LIMIT = 9999;
const INPUT_DEBOUNCE_TIME = 500;

export default function useChannelMembers({ groupId } = {}) {
  const { t: getLabel } = useTranslation();

  const [allMembers, setAllMembers] = useState([]);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef(null);

  const fetchAll = async (searchValue) => {
    if (!groupId) return;

    // Cancel previous in-flight request
    if (abortRef.current) abortRef.current.cancelled = true;
    const thisRequest = { cancelled: false };
    abortRef.current = thisRequest;

    setIsLoading(true);

    try {
      const filterCondition = { state: SystemConstant.ACTIVE_STATUS.active };
      const params = {
        groupId,
        state: SystemConstant.ACTIVE_STATUS.active,
        filter: JSON.stringify(filterCondition),
        limit: MAX_FETCH_LIMIT,
        offset: 0,
        order_by: "type",
      };
      if (searchValue) params.search = searchValue;

      const payload = qs.stringify(formatPagingParams(formatArray2Key(params)));
      const response = await ApiUtils.createApiWithToken(false).post(
        ApiConstant.POST_GET_CONVERSATION_MEMBER,
        payload,
      );

      if (thisRequest.cancelled) return;

      const camelized = HumpUtils.camelizeKeys(response.data);

      if (camelized?.status === ApiConstant.STT_OK) {
        const rawList = camelized.data?.data || [];
        setAllMembers(
          rawList.map(item => ({
            ...item,
            isAdmin: item.type === 0, // admin type
          })),
        );
        setTotalAdmins(camelized.data?.totalAdmin || 0);
      } else {
        setAllMembers([]);
      }
    } catch (error) {
      if (!thisRequest.cancelled) {
        SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
      }
    } finally {
      if (!thisRequest.cancelled) setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAll("");
  }, [groupId]);

  const handleSearch = useCallback(
    debounce(value => {
      fetchAll(value);
    }, INPUT_DEBOUNCE_TIME),
    [groupId],
  );

  const handleRefreshQuery = () => {
    fetchAll("");
  };

  return {
    allMembers,
    totalMembers: allMembers.length,
    totalAdmins,
    isLoading,
    handleSearch,
    handleRefreshQuery,
  };
}
